import { randomBytes, randomUUID, timingSafeEqual } from "node:crypto";
import fs from "node:fs/promises";
import http, { type IncomingMessage, type ServerResponse } from "node:http";
import path from "node:path";
import type { AddressInfo } from "node:net";
import type { AgentRuntime, NoriConfig, ProviderRegistry } from "@mewbleh/nori-core";
import { appendCommandLog, deleteOpenAiOAuthCredential, ensureWorkspace, hookEnabled, listWorkspaceSkills, mergeConfig, openAiOAuthStatus, readWorkspaceSkill, saveSessionMemory, searchWorkspaceSkills, writeConfig } from "@mewbleh/nori-core";
import type { AgentStore, CronSchedule, NoriPaths, SharedStore, StoredCronJob, StoredSession, StoredTask } from "@mewbleh/nori-persistence";
import {
  CORE_EVENTS,
  CORE_METHODS,
  failure,
  GatewayProtocolError,
  isConnectParams,
  isRequestFrame,
  MAX_BUFFERED_BYTES,
  MAX_PAYLOAD_BYTES,
  MIN_CLIENT_PROTOCOL_VERSION,
  PROTOCOL_VERSION,
  success,
  TICK_INTERVAL_MS,
  type ConnectParams,
  type EventFrame,
  type HelloOk,
  type RequestFrame,
  type ResponseFrame,
} from "@mewbleh/nori-protocol";
import type { ChannelAdapter } from "@mewbleh/nori-plugin-sdk";
import { WebSocketServer, WebSocket, type RawData } from "ws";
import { Logger } from "./logger.js";
import { nextScheduleTime, validateSchedule } from "./scheduler.js";
import { verifyConnectDevice } from "./device-auth.js";

export type ChannelStatus = { id: string; accountId: string; running: boolean; configured: boolean; message?: string };
export interface ChannelController {
  status(): ChannelStatus[];
  probe(channel: string, accountId?: string): Promise<{ ok: boolean; message?: string }>;
  start(channel?: string): Promise<ChannelStatus[]>;
  stop(channel?: string): Promise<ChannelStatus[]>;
  logout(channel: string, accountId?: string): Promise<void>;
  send(channel: string, accountId: string, message: { conversation: { channel: string; accountId: string; id: string; kind: "dm" | "group" | "thread"; threadId?: string }; text: string; replyToId?: string }): Promise<{ messageId: string }>;
  adapters(): ChannelAdapter[];
}

export type GatewayOptions = {
  config: NoriConfig;
  paths: NoriPaths;
  runtime: AgentRuntime;
  agentStore: AgentStore;
  agentStores?: Map<string, AgentStore>;
  sharedStore: SharedStore;
  providers: ProviderRegistry;
  authToken: string;
  channels?: ChannelController;
  logger?: Logger;
  version?: string;
};

type ConnectionState = {
  id: string;
  nonce: string;
  authenticated: boolean;
  role: string;
  scopes: string[];
  sequence: number;
  deviceId?: string;
  clientId?: string;
  displayName?: string;
  commands?: string[];
};

type RunResult = { done: Promise<{ text: string; error?: string }>; abort: () => boolean };

function tokenMatches(expected: string, actual: string | undefined): boolean {
  if (!actual) return false;
  const expectedBuffer = Buffer.from(expected);
  const actualBuffer = Buffer.from(actual);
  return expectedBuffer.length === actualBuffer.length && timingSafeEqual(expectedBuffer, actualBuffer);
}

function json(response: ServerResponse, status: number, body: unknown): void {
  response.writeHead(status, {
    "content-type": "application/json; charset=utf-8",
    "cache-control": "no-store",
    "x-content-type-options": "nosniff",
    "content-security-policy": "default-src 'none'; frame-ancestors 'none'",
    "referrer-policy": "no-referrer",
    "x-frame-options": "DENY",
  });
  response.end(`${JSON.stringify(body)}\n`);
}

function safeParse(data: RawData): unknown {
  const text = typeof data === "string" ? data : data.toString();
  if (Buffer.byteLength(text) > MAX_PAYLOAD_BYTES) throw new GatewayProtocolError("PAYLOAD_TOO_LARGE", "Gateway frame exceeds the payload limit");
  return JSON.parse(text) as unknown;
}

function parseSchedule(value: unknown): CronSchedule {
  if (!value || typeof value !== "object") throw new GatewayProtocolError("INVALID_PARAMS", "schedule is required");
  const candidate = value as Record<string, unknown>;
  let schedule: CronSchedule;
  if (candidate.kind === "at") schedule = { kind: "at", atMs: Number(candidate.atMs) };
  else if (candidate.kind === "every") schedule = { kind: "every", everyMs: Number(candidate.everyMs) };
  else if (candidate.kind === "cron") schedule = { kind: "cron", expr: String(candidate.expr ?? ""), ...(candidate.tz ? { tz: String(candidate.tz) as "UTC" } : {}) };
  else throw new GatewayProtocolError("INVALID_PARAMS", "schedule.kind must be at, every, or cron");
  try {
    return validateSchedule(schedule);
  } catch (error) {
    throw new GatewayProtocolError("INVALID_PARAMS", (error as Error).message);
  }
}

function taskStatus(value: unknown): StoredTask["status"] | undefined {
  const status = value == null ? undefined : String(value) as StoredTask["status"];
  if (status && !["queued", "running", "completed", "failed", "cancelled", "timed_out"].includes(status)) {
    throw new GatewayProtocolError("INVALID_PARAMS", `Invalid task status: ${status}`);
  }
  return status;
}

function boundedParam(value: unknown, label: string, maxBytes: number): string {
  const text = String(value ?? "");
  if (Buffer.byteLength(text) > maxBytes) throw new GatewayProtocolError("INVALID_PARAMS", `${label} exceeds ${maxBytes} bytes`);
  return text;
}

export class NoriGateway {
  readonly logger: Logger;
  readonly server: http.Server;
  readonly webSocketServer: WebSocketServer;
  readonly startedAt = Date.now();
  readonly connections = new Set<WebSocket>();
  readonly runs = new Map<string, RunResult>();
  #tick?: NodeJS.Timeout;
  #scheduler?: NodeJS.Timeout;
  #lockPath: string | undefined;
  #ready = false;
  #stopping?: Promise<void>;
  readonly #cronRunning = new Set<string>();
  readonly #taskCancels = new Set<string>();
  readonly #taskSessions = new Map<string, string>();
  readonly #nodeInvokes = new Map<string, { resolve(value: unknown): void; reject(error: Error): void; timer: NodeJS.Timeout }>();

  #stores(): AgentStore[] {
    return this.options.agentStores ? [...this.options.agentStores.values()] : [this.options.agentStore];
  }

  #sessions(options: { limit?: number; offset?: number; archived?: boolean; search?: string; activeMinutes?: number; agentId?: string } = {}) {
    const limit = Math.max(1, Math.min(options.limit ?? 100, 1000));
    const offset = Math.max(0, options.offset ?? 0);
    const stores = options.agentId && this.options.agentStores
      ? [this.options.agentStores.get(options.agentId)].filter((store): store is AgentStore => Boolean(store))
      : this.#stores();
    return stores
      .flatMap((store) => store.listSessionsFiltered({
        limit: limit + offset,
        archived: options.archived ?? false,
        ...(options.search ? { search: options.search } : {}),
        ...(options.activeMinutes ? { activeMinutes: options.activeMinutes } : {}),
      }))
      .sort((left, right) => Number(right.pinned) - Number(left.pinned) || right.updatedAt - left.updatedAt)
      .slice(offset, offset + limit);
  }

  #findSession(params: Record<string, unknown>): { store: AgentStore; session: StoredSession } | undefined {
    const selector = {
      ...(params.key || params.sessionKey ? { key: boundedParam(params.key ?? params.sessionKey, "session key", 512) } : {}),
      ...(params.sessionId ? { sessionId: boundedParam(params.sessionId, "session id", 128) } : {}),
      ...(params.label ? { label: boundedParam(params.label, "session label", 512) } : {}),
    };
    const stores = params.agentId && this.options.agentStores
      ? [this.options.agentStores.get(String(params.agentId))].filter((store): store is AgentStore => Boolean(store))
      : this.#stores();
    const matches = stores.flatMap((store) => {
      const session = store.findSession(selector);
      return session ? [{ store, session }] : [];
    });
    if (matches.length > 1) throw new GatewayProtocolError("CONFLICT", "Session selector matches more than one agent; specify agentId");
    return matches[0];
  }

  #workspaceFor(agentId = "main"): string {
    const agent = this.options.config.agents.list.find((entry) => entry.id === agentId);
    if (!agent) throw new GatewayProtocolError("NOT_FOUND", `Unknown agent: ${agentId}`);
    return agent.workspace ?? this.options.config.agents.defaults.workspace;
  }

  constructor(readonly options: GatewayOptions) {
    this.logger = options.logger ?? new Logger(options.paths.logsDir);
    this.server = http.createServer({ maxHeaderSize: 16 * 1024, requestTimeout: 10_000 }, (request, response) => this.#handleHttp(request, response));
    this.server.headersTimeout = 10_000;
    this.server.keepAliveTimeout = 5_000;
    this.webSocketServer = new WebSocketServer({ noServer: true, maxPayload: MAX_PAYLOAD_BYTES });
    this.server.on("upgrade", (request, socket, head) => {
      let pathname: string;
      try { pathname = new URL(request.url ?? "/", `http://${request.headers.host ?? "localhost"}`).pathname; }
      catch {
        socket.end("HTTP/1.1 400 Bad Request\r\nConnection: close\r\nContent-Length: 0\r\n\r\n");
        return;
      }
      if (pathname !== "/" && pathname !== "/ws") {
        socket.end("HTTP/1.1 404 Not Found\r\nConnection: close\r\nContent-Length: 0\r\n\r\n");
        return;
      }
      this.webSocketServer.handleUpgrade(request, socket, head, (webSocket) => {
        this.webSocketServer.emit("connection", webSocket, request);
      });
    });
    this.webSocketServer.on("connection", (webSocket) => this.#handleConnection(webSocket));
  }

  async #acquireLock(): Promise<void> {
    const lockPath = path.join(this.options.paths.stateDir, "gateway.lock");
    await fs.mkdir(path.dirname(lockPath), { recursive: true, mode: 0o700 });
    try {
      await fs.writeFile(lockPath, `${process.pid}\n`, { flag: "wx", mode: 0o600 });
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== "EEXIST") throw error;
      const pid = Number.parseInt(await fs.readFile(lockPath, "utf8"), 10);
      try {
        process.kill(pid, 0);
        throw new Error(`A Nori gateway is already running with PID ${pid}`);
      } catch (probeError) {
        if ((probeError as NodeJS.ErrnoException).code !== "ESRCH") throw probeError;
        await fs.rm(lockPath, { force: true });
        await fs.writeFile(lockPath, `${process.pid}\n`, { flag: "wx", mode: 0o600 });
      }
    }
    this.#lockPath = lockPath;
  }

  async start(): Promise<{ host: string; port: number }> {
    await this.#acquireLock();
    const recoveredTasks = this.options.sharedStore.recoverRunningTasks();
    try {
      await new Promise<void>((resolve, reject) => {
        this.server.once("error", reject);
        this.server.listen(this.options.config.gateway.port, this.options.config.gateway.bind, () => {
          this.server.off("error", reject);
          resolve();
        });
      });
    } catch (error) {
      if (this.#lockPath) await fs.rm(this.#lockPath, { force: true });
      this.#lockPath = undefined;
      throw error;
    }
    this.#ready = true;
    this.#tick = setInterval(() => this.broadcast("tick", { ts: Date.now() }), TICK_INTERVAL_MS);
    this.#tick.unref();
    this.#scheduler = setInterval(() => void this.#runDueCronJobs(), 1_000);
    this.#scheduler.unref();
    const address = this.server.address() as AddressInfo;
    this.logger.log("info", "Nori gateway started", { address: address.address, port: address.port, protocol: PROTOCOL_VERSION, recoveredTasks });
    void this.#runBootHooks();
    return { host: address.address, port: address.port };
  }

  async stop(reason = "shutdown"): Promise<void> {
    if (this.#stopping) return this.#stopping;
    this.#stopping = this.#stop(reason);
    return this.#stopping;
  }

  async #stop(reason: string): Promise<void> {
    this.#ready = false;
    if (this.#tick) clearInterval(this.#tick);
    if (this.#scheduler) clearInterval(this.#scheduler);
    this.broadcast("shutdown", { reason });
    for (const run of this.runs.values()) run.abort();
    for (const [invokeId, pending] of this.#nodeInvokes) {
      clearTimeout(pending.timer);
      pending.reject(new GatewayProtocolError("SHUTTING_DOWN", "Gateway is shutting down", { invokeId }, true));
    }
    this.#nodeInvokes.clear();
    try {
      await this.options.channels?.stop();
      await Promise.all([...this.connections].map((webSocket) => new Promise<void>((resolve) => {
        if (webSocket.readyState === WebSocket.CLOSED) return resolve();
        const timer = setTimeout(() => { webSocket.terminate(); resolve(); }, 2_000);
        webSocket.once("close", () => { clearTimeout(timer); resolve(); });
        webSocket.close(1001, reason);
      })));
      if (this.server.listening) await new Promise<void>((resolve, reject) => this.server.close((error) => error ? reject(error) : resolve()));
      await new Promise<void>((resolve) => this.webSocketServer.close(() => resolve()));
      this.logger.log("info", "Nori gateway stopped", { reason });
    } finally {
      if (this.#lockPath) await fs.rm(this.#lockPath, { force: true });
      this.#lockPath = undefined;
    }
  }

  address(): { host: string; port: number } | undefined {
    const value = this.server.address();
    if (!value || typeof value === "string") return undefined;
    return { host: value.address, port: value.port };
  }

  #handleHttp(request: IncomingMessage, response: ServerResponse): void {
    let url: URL;
    try { url = new URL(request.url ?? "/", `http://${request.headers.host ?? "localhost"}`); }
    catch { json(response, 400, { error: "bad_request", message: "Invalid request URL" }); return; }
    if (request.method === "GET" && url.pathname === "/healthz") {
      json(response, 200, { ok: true, service: "nori-gateway", protocol: PROTOCOL_VERSION });
      return;
    }
    if (request.method === "GET" && url.pathname === "/readyz") {
      json(response, this.#ready ? 200 : 503, { ok: this.#ready, uptimeMs: Date.now() - this.startedAt });
      return;
    }
    json(response, 404, { error: "not_found", message: "Nori has no web UI" });
  }

  #send(webSocket: WebSocket, frame: ResponseFrame | EventFrame): void {
    if (webSocket.readyState !== WebSocket.OPEN) return;
    if (webSocket.bufferedAmount > MAX_BUFFERED_BYTES) {
      webSocket.close(1009, "backpressure limit exceeded");
      return;
    }
    webSocket.send(JSON.stringify(frame));
  }

  broadcast(event: string, payload?: unknown, requiredScope?: string): void {
    for (const webSocket of this.connections) {
      const state = (webSocket as WebSocket & { nori?: ConnectionState }).nori;
      if (!state?.authenticated) continue;
      if (requiredScope && !state.scopes.includes(requiredScope)) continue;
      state.sequence += 1;
      this.#send(webSocket, { type: "event", event, ...(payload === undefined ? {} : { payload }), seq: state.sequence });
    }
  }

  #handleConnection(webSocket: WebSocket): void {
    const state: ConnectionState = {
      id: randomUUID(),
      nonce: randomBytes(24).toString("base64url"),
      authenticated: false,
      role: "operator",
      scopes: [],
      sequence: 0,
    };
    (webSocket as WebSocket & { nori?: ConnectionState }).nori = state;
    this.connections.add(webSocket);
    this.#send(webSocket, { type: "event", event: "connect.challenge", payload: { nonce: state.nonce, ts: Date.now() } });
    const timeout = setTimeout(() => webSocket.close(1008, "connect timeout"), 15_000);
    webSocket.on("message", (data) => {
      void (async () => {
        let request: RequestFrame | undefined;
        try {
          const parsed = safeParse(data);
          if (!isRequestFrame(parsed)) throw new GatewayProtocolError("INVALID_FRAME", "Expected a request frame");
          request = parsed;
          if (!state.authenticated && request.method !== "connect") throw new GatewayProtocolError("UNAUTHORIZED", "Connect before calling gateway methods");
          let payload: unknown;
          if (request.method === "connect") {
            if (!isConnectParams(request.params)) throw new GatewayProtocolError("INVALID_PARAMS", "Connect parameters do not match protocol v4");
            payload = this.#connect(request.params, state);
          } else {
            payload = await this.#dispatch(request, state);
          }
          if (request.method === "connect") clearTimeout(timeout);
          this.#send(webSocket, success(request.id, payload));
        } catch (error) {
          const candidate = error as Error & { code?: string; details?: unknown; retryable?: boolean };
          this.logger.log("warn", "Gateway request failed", { method: request?.method, error: candidate.message });
          this.#send(
            webSocket,
            failure(request?.id ?? "invalid", {
              code: candidate.code ?? "INTERNAL",
              message: candidate.message,
              ...(candidate.details === undefined ? {} : { details: candidate.details }),
              ...(candidate.retryable === undefined ? {} : { retryable: candidate.retryable }),
            }),
          );
        }
      })();
    });
    webSocket.on("close", () => {
      clearTimeout(timeout);
      this.connections.delete(webSocket);
    });
    webSocket.on("error", (error) => this.logger.log("warn", "WebSocket connection error", { message: error.message }));
  }

  #connect(params: ConnectParams, state: ConnectionState): HelloOk {
    if (!params || params.minProtocol > PROTOCOL_VERSION || params.maxProtocol < MIN_CLIENT_PROTOCOL_VERSION) {
      throw new GatewayProtocolError("PROTOCOL_MISMATCH", `Nori requires gateway protocol ${PROTOCOL_VERSION}`);
    }
    const requestedRole = params.role ?? "operator";
    const requestedScopes = params.scopes ?? [];
    if (tokenMatches(this.options.authToken, params.auth?.token)) {
      state.role = requestedRole;
      state.scopes = requestedScopes.length ? requestedScopes : ["operator.read", "operator.write", "operator.admin", "operator.approvals", "operator.pairing"];
    } else if (params.auth?.deviceToken) {
      const device = params.device;
      if (!device) throw new GatewayProtocolError("UNAUTHORIZED", "Device identity is required", { reason: "device-signature" });
      const identity = verifyConnectDevice(params, state.nonce, params.auth.deviceToken);
      if (!identity.ok) throw new GatewayProtocolError("UNAUTHORIZED", identity.message, { reason: "device-signature" });
      const authenticated = this.options.sharedStore.authenticateDeviceToken(params.auth.deviceToken);
      if (!authenticated || authenticated.deviceId !== device.id || authenticated.publicKey !== device.publicKey) throw new GatewayProtocolError("UNAUTHORIZED", "Invalid device token", { reason: "device-token" });
      if (authenticated.role !== requestedRole || requestedScopes.some((scope) => !authenticated.scopes.includes(scope))) throw new GatewayProtocolError("FORBIDDEN", "Device token does not grant the requested role/scopes", { reason: "device-scope" });
      state.role = authenticated.role;
      state.scopes = requestedScopes.length ? requestedScopes : authenticated.scopes;
      state.deviceId = authenticated.deviceId;
      this.options.sharedStore.touchDevice(authenticated.deviceId);
    } else if (params.device) {
      const identity = verifyConnectDevice(params, state.nonce);
      if (!identity.ok) throw new GatewayProtocolError("UNAUTHORIZED", identity.message, { reason: "device-signature" });
      const requestId = this.options.sharedStore.requestDevicePairing({
        deviceId: params.device.id,
        ...(params.client.displayName ? { displayName: params.client.displayName } : {}),
        publicKey: params.device.publicKey,
        role: requestedRole,
        scopes: requestedScopes,
      });
      throw new GatewayProtocolError("PAIRING_REQUIRED", "Device pairing approval is required", { requestId, deviceId: params.device.id });
    } else {
      throw new GatewayProtocolError("UNAUTHORIZED", "Invalid gateway token");
    }
    state.authenticated = true;
    state.clientId = params.client.id;
    if (params.client.displayName) state.displayName = params.client.displayName;
    if (params.commands) state.commands = params.commands;
    return {
      type: "hello-ok",
      protocol: PROTOCOL_VERSION,
      server: { version: this.options.version ?? "0.3.0", connId: state.id },
      features: { methods: [...CORE_METHODS], events: [...CORE_EVENTS], capabilities: ["tool-events"] },
      snapshot: {
        uptimeMs: Date.now() - this.startedAt,
        channels: this.options.channels?.status() ?? [],
        agents: this.options.config.agents.list.map((agent) => ({ id: agent.id })),
      },
      auth: { role: state.role, scopes: state.scopes },
      policy: { maxPayload: MAX_PAYLOAD_BYTES, maxBufferedBytes: MAX_BUFFERED_BYTES, tickIntervalMs: TICK_INTERVAL_MS },
    };
  }

  async #dispatch(request: RequestFrame, state: ConnectionState): Promise<unknown> {
    const params = (request.params ?? {}) as Record<string, unknown>;
    if (hookEnabled(this.options.config, "command-logger")) {
      void appendCommandLog(this.options.paths, { method: request.method, params, role: state.role }).catch((error) => this.logger.log("warn", "Command hook failed", { message: (error as Error).message }));
    }
    const readOnly = new Set([
      "health", "status", "agent.wait", "chat.history", "sessions.list", "sessions.preview", "sessions.describe", "sessions.resolve", "sessions.get", "tools.catalog", "tools.effective", "skills.status", "skills.skillCard", "skills.search", "skills.detail", "models.list", "models.probe", "models.auth.status", "memory.status", "memory.search", "mcp.status", "cron.get", "cron.list", "cron.runs", "cron.status", "tasks.list", "tasks.get", "node.list", "node.describe",
      "agents.list", "channels.status", "channels.probe", "config.get", "logs.tail", "pairing.list", "plugins.list", "approvals.list",
    ]);
    const adminOnly = new Set(["sessions.reset", "sessions.compact", "memory.reindex", "mcp.reload", "cron.remove", "device.token.rotate", "device.token.revoke"]);
    const requiredScope = request.method === "node.invoke.result" && state.role === "node"
      ? "node"
      : request.method.startsWith("device.pair.") || request.method.startsWith("node.pair.")
        ? "operator.pairing"
        : request.method === "approvals.list" || request.method === "approvals.resolve"
      ? "operator.approvals"
      : adminOnly.has(request.method) || (request.method === "sessions.delete" && params.archivedOnly !== true)
      ? "operator.admin"
      : readOnly.has(request.method) ? "operator.read" : "operator.write";
    if (requiredScope !== state.role && !state.scopes.includes(requiredScope)) {
      throw new GatewayProtocolError("FORBIDDEN", `Gateway method ${request.method} requires ${requiredScope}`);
    }
    switch (request.method) {
      case "health":
        return { ok: true, protocol: PROTOCOL_VERSION, uptimeMs: Date.now() - this.startedAt };
      case "status":
        return {
          gateway: { running: true, ...this.address(), connections: this.connections.size },
          agents: this.options.config.agents.list,
          channels: this.options.channels?.status() ?? [],
          sessions: this.#sessions({ limit: 10 }),
        };
      case "agent":
      case "chat.send":
        return this.#startAgent(params);
      case "agent.wait": {
        const run = this.runs.get(String(params.runId ?? ""));
        if (!run) throw new GatewayProtocolError("NOT_FOUND", "Unknown agent run");
        return run.done;
      }
      case "chat.abort": {
        const sessionKey = String(params.sessionKey ?? "agent:main:main");
        return { aborted: this.options.runtime.abort(sessionKey) };
      }
      case "chat.history": {
        const match = this.#findSession(params);
        if (match) return { messages: match.store.history(match.session.sessionId, Number(params.limit ?? 100)) };
        return { messages: [] };
      }
      case "sessions.list": {
        const sessions = this.#sessions({
          limit: Number(params.limit ?? 100),
          offset: Number(params.offset ?? 0),
          archived: params.archived === true,
          ...(params.search ? { search: boundedParam(params.search, "session search", 32 * 1024) } : {}),
          ...(params.activeMinutes ? { activeMinutes: Number(params.activeMinutes) } : {}),
          ...(params.agentId ? { agentId: String(params.agentId) } : {}),
        });
        return { sessions, count: sessions.length };
      }
      case "sessions.preview": {
        const keys = Array.isArray(params.keys) ? params.keys.map(String) : params.key || params.sessionKey ? [String(params.key ?? params.sessionKey)] : [];
        const limit = Math.max(1, Math.min(Number(params.limit ?? 5), 50));
        const maxChars = Math.max(32, Math.min(Number(params.maxChars ?? 500), 10_000));
        return {
          ts: Date.now(),
          previews: keys.map((key) => {
            const match = this.#findSession({ key, ...(params.agentId ? { agentId: params.agentId } : {}) });
            if (!match) return { key, status: "missing", items: [] };
            const items = match.store.history(match.session.sessionId, limit).map((entry) => ({
              role: entry.role,
              text: (typeof entry.content === "string" ? entry.content : JSON.stringify(entry.content)).slice(0, maxChars),
              ts: entry.createdAt,
            }));
            return { key, status: items.length ? "ok" : "empty", items };
          }),
        };
      }
      case "sessions.describe": {
        const match = this.#findSession(params);
        if (!match) throw new GatewayProtocolError("NOT_FOUND", "Unknown session");
        const history = match.store.history(match.session.sessionId, 1);
        return { session: match.session, messageCount: match.store.transcriptCount(match.session.sessionId), lastMessage: history.at(-1) };
      }
      case "sessions.resolve": {
        const match = this.#findSession(params);
        if (!match) {
          if (params.allowMissing === true) return { ok: false };
          throw new GatewayProtocolError("NOT_FOUND", "Unknown session");
        }
        return { ok: true, key: match.session.sessionKey, sessionId: match.session.sessionId, agentId: match.session.agentId };
      }
      case "sessions.get": {
        const match = this.#findSession(params);
        if (!match) throw new GatewayProtocolError("NOT_FOUND", "Unknown session");
        return { session: match.session, messages: match.store.history(match.session.sessionId, Number(params.limit ?? 100)) };
      }
      case "sessions.patch": {
        const match = this.#findSession(params);
        if (!match) throw new GatewayProtocolError("NOT_FOUND", "Unknown session");
        const entry = match.store.patchSession(match.session.sessionKey, {
          ...(Object.hasOwn(params, "label") ? { label: params.label == null ? null : boundedParam(params.label, "label", 512) } : {}),
          ...(Object.hasOwn(params, "category") ? { category: params.category == null ? null : boundedParam(params.category, "category", 256) } : {}),
          ...(Object.hasOwn(params, "model") ? { model: params.model == null ? null : boundedParam(params.model, "model", 512) } : {}),
          ...(Object.hasOwn(params, "archived") ? { archived: params.archived === true } : {}),
          ...(Object.hasOwn(params, "pinned") ? { pinned: params.pinned === true } : {}),
          ...(Object.hasOwn(params, "unread") ? { unread: params.unread === true } : {}),
        });
        this.broadcast("sessions.changed", { action: "patch", key: match.session.sessionKey, entry });
        return { ok: true, key: match.session.sessionKey, entry };
      }
      case "sessions.reset": {
        const match = this.#findSession(params);
        if (!match) throw new GatewayProtocolError("NOT_FOUND", "Unknown session");
        let memoryFile: string | undefined;
        if (hookEnabled(this.options.config, "session-memory")) {
          memoryFile = await saveSessionMemory(
            this.#workspaceFor(match.session.agentId),
            match.session.sessionKey,
            match.store.history(match.session.sessionId, 200),
            this.options.config.hooks.internal.entries["session-memory"]?.messages ?? 15,
          );
        }
        const entry = match.store.resetSession(match.session.sessionKey);
        this.broadcast("sessions.changed", { action: "reset", key: match.session.sessionKey, entry, ...(memoryFile ? { memoryFile } : {}) });
        return { ok: true, key: match.session.sessionKey, entry, ...(memoryFile ? { memoryFile } : {}) };
      }
      case "sessions.delete": {
        const match = this.#findSession(params);
        if (!match) throw new GatewayProtocolError("NOT_FOUND", "Unknown session");
        if (params.archivedOnly === true && !match.session.archived) throw new GatewayProtocolError("FORBIDDEN", "Session must be archived before deletion");
        const deleted = match.store.deleteSession(match.session.sessionKey, params.deleteTranscript !== false);
        this.broadcast("sessions.changed", { action: "delete", key: match.session.sessionKey });
        return { ok: true, key: match.session.sessionKey, deleted };
      }
      case "sessions.compact": {
        const match = this.#findSession(params);
        if (!match) throw new GatewayProtocolError("NOT_FOUND", "Unknown session");
        if (hookEnabled(this.options.config, "compaction-notifier")) this.broadcast("hook", { name: "compaction-notifier", event: "session:compact:before", sessionKey: match.session.sessionKey, messageCount: match.store.transcriptCount(match.session.sessionId) });
        const result = match.store.compactSession(match.session.sessionKey, Number(params.maxLines ?? 60));
        if (hookEnabled(this.options.config, "compaction-notifier")) this.broadcast("hook", { name: "compaction-notifier", event: "session:compact:after", sessionKey: match.session.sessionKey, result });
        this.broadcast("sessions.changed", { action: "compact", key: match.session.sessionKey, result });
        return { ok: true, key: match.session.sessionKey, ...result };
      }
      case "tools.catalog":
        return this.options.runtime.toolCatalog(params.agentId ? String(params.agentId) : "main");
      case "tools.effective": {
        const sessionKey = String(params.sessionKey ?? "");
        if (!sessionKey) throw new GatewayProtocolError("INVALID_PARAMS", "sessionKey is required");
        return this.options.runtime.effectiveTools(params.agentId ? String(params.agentId) : "main", sessionKey);
      }
      case "tools.invoke": {
        const name = String(params.name ?? "");
        if (!name) throw new GatewayProtocolError("INVALID_PARAMS", "name is required");
        const result = await this.options.runtime.invokeTool({
          name,
          ...(Object.hasOwn(params, "args") ? { args: params.args } : {}),
          ...(params.sessionKey ? { sessionKey: String(params.sessionKey) } : {}),
          ...(params.agentId ? { agentId: String(params.agentId) } : {}),
          ...(params.confirm === true ? { confirm: true } : {}),
          ...(params.idempotencyKey ? { idempotencyKey: String(params.idempotencyKey) } : {}),
        });
        if (!result.ok && result.approvalId) this.broadcast("exec.approval.requested", { id: result.approvalId, tool: name }, "operator.approvals");
        return result;
      }
      case "skills.status": {
        const agentId = params.agentId ? String(params.agentId) : "main";
        const workspace = this.#workspaceFor(agentId);
        const skills = await listWorkspaceSkills(workspace);
        return {
          agentId,
          workspace,
          skills: skills.map((skill) => ({ ...skill, skillKey: skill.key, baseDir: path.dirname(skill.path), skillCard: { path: skill.path, sizeBytes: skill.size } })),
        };
      }
      case "skills.skillCard": {
        const key = String(params.skillKey ?? "");
        if (!key) throw new GatewayProtocolError("INVALID_PARAMS", "skillKey is required");
        try {
          const skill = await readWorkspaceSkill(this.#workspaceFor(params.agentId ? String(params.agentId) : "main"), key);
          return { schema: "openclaw.skills.skill-card.v1", skillKey: key, path: skill.path, sizeBytes: skill.size, content: skill.content };
        } catch (error) {
          throw new GatewayProtocolError("NOT_FOUND", (error as Error).message);
        }
      }
      case "skills.search": {
        const workspace = this.#workspaceFor(params.agentId ? String(params.agentId) : "main");
        const results = await searchWorkspaceSkills(workspace, String(params.query ?? ""));
        return { results: results.slice(0, Math.max(1, Math.min(Number(params.limit ?? 25), 100))) };
      }
      case "skills.detail": {
        const key = String(params.slug ?? params.skillKey ?? "");
        if (!key) throw new GatewayProtocolError("INVALID_PARAMS", "slug is required");
        try {
          const skill = await readWorkspaceSkill(this.#workspaceFor(params.agentId ? String(params.agentId) : "main"), key);
          return { skill: { slug: skill.key, displayName: skill.name, summary: skill.description }, local: true, path: skill.path, content: skill.content };
        } catch (error) {
          throw new GatewayProtocolError("NOT_FOUND", (error as Error).message);
        }
      }
      case "agents.list":
        return { agents: this.options.config.agents.list };
      case "agents.create": {
        const id = String(params.id ?? "");
        if (!/^[a-z0-9][a-z0-9_-]{0,63}$/u.test(id)) throw new GatewayProtocolError("INVALID_PARAMS", "Invalid agent id");
        if (this.options.config.agents.list.some((agent) => agent.id === id)) throw new GatewayProtocolError("CONFLICT", `Agent already exists: ${id}`);
        const workspace = params.workspace ? path.resolve(String(params.workspace)) : path.join(this.options.paths.stateDir, "workspaces", id);
        await ensureWorkspace(workspace);
        this.options.config.agents.list.push({ id, workspace, ...(params.model ? { model: String(params.model) } : {}) });
        await writeConfig(this.options.paths, this.options.config);
        return { agent: this.options.config.agents.list.at(-1) };
      }
      case "agents.update": {
        const agent = this.options.config.agents.list.find((entry) => entry.id === String(params.id ?? ""));
        if (!agent) throw new GatewayProtocolError("NOT_FOUND", "Unknown agent");
        if (params.workspace) {
          agent.workspace = path.resolve(String(params.workspace));
          await ensureWorkspace(agent.workspace);
        }
        if (params.model) agent.model = String(params.model);
        await writeConfig(this.options.paths, this.options.config);
        return { agent };
      }
      case "agents.delete": {
        const id = String(params.id ?? "");
        if (id === "main") throw new GatewayProtocolError("INVALID_PARAMS", "The main agent cannot be deleted");
        const before = this.options.config.agents.list.length;
        this.options.config.agents.list = this.options.config.agents.list.filter((agent) => agent.id !== id);
        if (before === this.options.config.agents.list.length) throw new GatewayProtocolError("NOT_FOUND", "Unknown agent");
        await writeConfig(this.options.paths, this.options.config);
        return { deleted: true, retainedState: true };
      }
      case "models.list": {
        const providers = [];
        for (const id of this.options.providers.providers.keys()) {
          const configured = Boolean(this.options.config.providers[id]) || id === "ollama";
          if (!configured) {
            providers.push({ id, configured, models: [] });
            continue;
          }
          try {
            providers.push({ id, configured, models: await this.options.runtime.listModels(id) });
          } catch (error) {
            providers.push({ id, configured, models: [], error: (error as Error).message });
          }
        }
        return { providers };
      }
      case "models.probe": {
        const id = String(params.provider ?? "");
        return this.options.runtime.probeProvider(id);
      }
      case "models.auth.status": {
        const provider = String(params.provider ?? "openai");
        if (provider !== "openai") throw new GatewayProtocolError("INVALID_PARAMS", "OAuth status is currently supported for openai");
        const store = params.agentId && this.options.agentStores
          ? this.options.agentStores.get(String(params.agentId))
          : this.options.agentStore;
        if (!store) throw new GatewayProtocolError("NOT_FOUND", "Unknown agent");
        return openAiOAuthStatus(store);
      }
      case "models.auth.logout": {
        const provider = String(params.provider ?? "openai");
        if (provider !== "openai") throw new GatewayProtocolError("INVALID_PARAMS", "OAuth logout is currently supported for openai");
        const store = params.agentId && this.options.agentStores
          ? this.options.agentStores.get(String(params.agentId))
          : this.options.agentStore;
        if (!store) throw new GatewayProtocolError("NOT_FOUND", "Unknown agent");
        return { provider, method: "oauth", removed: deleteOpenAiOAuthCredential(store) };
      }
      case "memory.status": {
        const store = params.agentId && this.options.agentStores ? this.options.agentStores.get(String(params.agentId)) : this.options.agentStore;
        if (!store) throw new GatewayProtocolError("NOT_FOUND", "Unknown agent");
        return { agentId: String(params.agentId ?? "main"), ...store.memoryStatus() };
      }
      case "memory.search": {
        const store = params.agentId && this.options.agentStores ? this.options.agentStores.get(String(params.agentId)) : this.options.agentStore;
        if (!store) throw new GatewayProtocolError("NOT_FOUND", "Unknown agent");
        const query = boundedParam(params.query, "query", 32 * 1024).trim();
        if (!query) throw new GatewayProtocolError("INVALID_PARAMS", "query is required");
        return { results: store.searchMemory(query, Number(params.limit ?? 10)) };
      }
      case "memory.reindex": {
        const store = params.agentId && this.options.agentStores ? this.options.agentStores.get(String(params.agentId)) : this.options.agentStore;
        if (!store) throw new GatewayProtocolError("NOT_FOUND", "Unknown agent");
        return { agentId: String(params.agentId ?? "main"), reindexed: store.reindexMemoryVectors(), ...store.memoryStatus() };
      }
      case "mcp.status":
        return { servers: this.options.runtime.mcpStatus() };
      case "mcp.reload":
        await this.options.runtime.reloadMcp();
        return { ok: true, servers: this.options.runtime.mcpStatus() };
      case "device.pair.list":
        return this.options.sharedStore.listDevices();
      case "node.pair.list": {
        const devices = this.options.sharedStore.listDevices();
        return { pending: devices.pending.filter((entry) => entry.role === "node"), paired: devices.paired.filter((entry) => Array.isArray(entry.roles) && entry.roles.includes("node")) };
      }
      case "device.pair.approve": {
        const result = this.options.sharedStore.approveDevicePairing(String(params.requestId ?? params.id ?? ""));
        if (!result) throw new GatewayProtocolError("NOT_FOUND", "Unknown or expired device pairing request");
        this.broadcast("hook", { event: "device:paired", deviceId: result.deviceId, role: result.role });
        return result;
      }
      case "node.pair.approve": {
        const requestId = String(params.requestId ?? params.id ?? "");
        if (!this.options.sharedStore.listDevices().pending.some((entry) => entry.requestId === requestId && entry.role === "node")) throw new GatewayProtocolError("NOT_FOUND", "Unknown or expired node pairing request");
        const result = this.options.sharedStore.approveDevicePairing(requestId)!;
        return result;
      }
      case "device.pair.reject":
      case "node.pair.reject":
        return { rejected: this.options.sharedStore.rejectDevicePairing(String(params.requestId ?? params.id ?? "")) };
      case "device.pair.remove":
      case "node.pair.remove":
        return { removed: this.options.sharedStore.removeDevice(String(params.deviceId ?? params.id ?? "")) };
      case "device.token.rotate": {
        const result = this.options.sharedStore.rotateDeviceToken(
          String(params.deviceId ?? params.device ?? ""),
          String(params.role ?? "operator"),
          Array.isArray(params.scopes) ? params.scopes.map(String) : undefined,
        );
        if (!result) throw new GatewayProtocolError("NOT_FOUND", "Unknown device role token");
        return { deviceId: String(params.deviceId ?? params.device), role: String(params.role ?? "operator"), ...result };
      }
      case "device.token.revoke":
        return { revoked: this.options.sharedStore.revokeDeviceToken(String(params.deviceId ?? params.device ?? ""), String(params.role ?? "operator")) };
      case "node.list": {
        const nodes = [...this.connections].flatMap((socket) => {
          const connection = (socket as WebSocket & { nori?: ConnectionState }).nori;
          return connection?.authenticated && connection.role === "node"
            ? [{ nodeId: connection.deviceId ?? connection.id, displayName: connection.displayName, connected: true, commands: connection.commands ?? [], clientId: connection.clientId }]
            : [];
        });
        return { nodes };
      }
      case "node.describe": {
        const id = String(params.nodeId ?? params.node ?? "");
        const result = await this.#dispatch({ ...request, method: "node.list" }, state) as { nodes: Array<Record<string, unknown>> };
        const node = result.nodes.find((entry) => entry.nodeId === id || entry.displayName === id);
        if (!node) throw new GatewayProtocolError("NOT_FOUND", "Unknown or disconnected node");
        return { node };
      }
      case "node.rename": {
        const id = String(params.nodeId ?? params.node ?? "");
        const name = String(params.name ?? "").trim();
        if (!id || !name) throw new GatewayProtocolError("INVALID_PARAMS", "nodeId and name are required");
        if (!this.options.sharedStore.renameDevice(id, name)) throw new GatewayProtocolError("NOT_FOUND", "Unknown node");
        for (const candidate of this.connections) {
          const connection = (candidate as WebSocket & { nori?: ConnectionState }).nori;
          if (connection?.deviceId === id) connection.displayName = name;
        }
        return { nodeId: id, displayName: name };
      }
      case "node.invoke": {
        const nodeId = String(params.nodeId ?? params.node ?? "");
        const command = String(params.command ?? "");
        if (!nodeId || !command) throw new GatewayProtocolError("INVALID_PARAMS", "nodeId and command are required");
        if (["system.run", "system.run.prepare"].includes(command)) throw new GatewayProtocolError("FORBIDDEN", `${command} is not available through direct node.invoke`);
        const socket = [...this.connections].find((candidate) => {
          const connection = (candidate as WebSocket & { nori?: ConnectionState }).nori;
          return connection?.authenticated && connection.role === "node" && (connection.deviceId === nodeId || connection.displayName === nodeId);
        });
        const connection = (socket as WebSocket & { nori?: ConnectionState } | undefined)?.nori;
        if (!socket || !connection) throw new GatewayProtocolError("NOT_FOUND", "Node is not connected");
        if (connection.commands?.length && !connection.commands.includes(command)) throw new GatewayProtocolError("FORBIDDEN", `Node did not declare command: ${command}`);
        const invokeId = randomUUID();
        const result = new Promise<unknown>((resolve, reject) => {
          const timer = setTimeout(() => {
            this.#nodeInvokes.delete(invokeId);
            reject(new GatewayProtocolError("TIMEOUT", "Node invoke timed out", undefined, true));
          }, Math.max(100, Math.min(Number(params.timeoutMs ?? 15_000), 120_000)));
          this.#nodeInvokes.set(invokeId, { resolve, reject, timer });
        });
        this.#send(socket, { type: "event", event: "node.invoke.request", payload: { invokeId, command, params: params.params ?? {}, idempotencyKey: params.idempotencyKey } });
        return result;
      }
      case "node.invoke.result": {
        const invokeId = String(params.invokeId ?? "");
        const pending = this.#nodeInvokes.get(invokeId);
        if (!pending) throw new GatewayProtocolError("NOT_FOUND", "Unknown node invocation");
        this.#nodeInvokes.delete(invokeId);
        clearTimeout(pending.timer);
        if (params.ok === false) pending.reject(new GatewayProtocolError(String((params.error as Record<string, unknown> | undefined)?.code ?? "NODE_ERROR"), String((params.error as Record<string, unknown> | undefined)?.message ?? "Node invocation failed")));
        else pending.resolve(params.result ?? params.payload ?? null);
        return { accepted: true };
      }
      case "cron.add": {
        const id = randomUUID();
        const agentId = String(params.agentId ?? "main");
        if (!this.options.config.agents.list.some((agent) => agent.id === agentId)) throw new GatewayProtocolError("NOT_FOUND", `Unknown agent: ${agentId}`);
        const payload = params.payload && typeof params.payload === "object" ? params.payload as Record<string, unknown> : {};
        const prompt = boundedParam(params.prompt ?? params.message ?? payload.message ?? payload.text, "cron prompt", 1024 * 1024).trim();
        if (!prompt) throw new GatewayProtocolError("INVALID_PARAMS", "prompt, message, or payload.message is required");
        const schedule = parseSchedule(params.schedule);
        const enabled = params.enabled !== false;
        const nextRunAtMs = enabled ? nextScheduleTime(schedule, Date.now()) : undefined;
        if (enabled && nextRunAtMs === undefined) throw new GatewayProtocolError("INVALID_PARAMS", "The one-shot schedule is in the past");
        const job = this.options.sharedStore.addCronJob({
          id,
          name: boundedParam(params.name ?? `cron-${id.slice(0, 8)}`, "cron name", 512),
          agentId,
          sessionKey: String(params.sessionKey ?? `agent:${agentId}:cron:${id}`),
          schedule,
          prompt,
          enabled,
          ...(nextRunAtMs === undefined ? {} : { nextRunAtMs }),
        });
        this.broadcast("cron", { action: "added", job });
        return { job };
      }
      case "cron.get": {
        const job = this.options.sharedStore.getCronJob(String(params.id ?? params.jobId ?? ""));
        if (!job) throw new GatewayProtocolError("NOT_FOUND", "Unknown cron job");
        return { job };
      }
      case "cron.list": {
        const jobs = this.options.sharedStore.listCronJobs({
          limit: Number(params.limit ?? 100),
          offset: Number(params.offset ?? 0),
          ...(params.enabled === undefined ? {} : { enabled: params.enabled === true }),
        });
        return { jobs, count: jobs.length };
      }
      case "cron.update": {
        const id = String(params.id ?? params.jobId ?? "");
        const existing = this.options.sharedStore.getCronJob(id);
        if (!existing) throw new GatewayProtocolError("NOT_FOUND", "Unknown cron job");
        const schedule = params.schedule === undefined ? existing.schedule : parseSchedule(params.schedule);
        const enabled = params.enabled === undefined ? existing.enabled : params.enabled === true;
        const shouldReschedule = params.schedule !== undefined || params.enabled !== undefined;
        const nextRunAtMs = shouldReschedule && enabled ? nextScheduleTime(schedule, Date.now()) : shouldReschedule ? undefined : existing.nextRunAtMs;
        if (enabled && shouldReschedule && nextRunAtMs === undefined) throw new GatewayProtocolError("INVALID_PARAMS", "The one-shot schedule is in the past");
        const job = this.options.sharedStore.updateCronJob(id, {
          ...(params.name === undefined ? {} : { name: boundedParam(params.name, "cron name", 512) }),
          ...(params.agentId === undefined ? {} : { agentId: String(params.agentId) }),
          ...(params.sessionKey === undefined ? {} : { sessionKey: String(params.sessionKey) }),
          ...(params.prompt === undefined && params.message === undefined ? {} : { prompt: boundedParam(params.prompt ?? params.message, "cron prompt", 1024 * 1024) }),
          ...(params.schedule === undefined ? {} : { schedule }),
          ...(params.enabled === undefined ? {} : { enabled }),
          ...(shouldReschedule ? { nextRunAtMs: nextRunAtMs ?? null } : {}),
        });
        this.broadcast("cron", { action: "updated", job });
        return { job };
      }
      case "cron.remove": {
        const id = String(params.id ?? params.jobId ?? "");
        if (this.#cronRunning.has(id)) throw new GatewayProtocolError("CONFLICT", "Cron job is currently running");
        const removed = this.options.sharedStore.removeCronJob(id);
        if (!removed) throw new GatewayProtocolError("NOT_FOUND", "Unknown cron job");
        this.broadcast("cron", { action: "removed", id });
        return { removed: true, id };
      }
      case "cron.run": {
        const job = this.options.sharedStore.getCronJob(String(params.id ?? params.jobId ?? ""));
        if (!job) throw new GatewayProtocolError("NOT_FOUND", "Unknown cron job");
        return this.#startCronJob(job, true);
      }
      case "cron.runs":
        return { runs: this.options.sharedStore.listCronRuns(params.id || params.jobId ? String(params.id ?? params.jobId) : undefined, Number(params.limit ?? 100)) };
      case "cron.status": {
        const jobs = this.options.sharedStore.listCronJobs({ limit: 1_000 });
        return {
          enabled: jobs.filter((job) => job.enabled).length,
          total: jobs.length,
          running: [...this.#cronRunning],
          nextRunAtMs: jobs.flatMap((job) => job.nextRunAtMs === undefined ? [] : [job.nextRunAtMs]).sort((left, right) => left - right)[0],
        };
      }
      case "tasks.list": {
        const status = taskStatus(params.status);
        const tasks = this.options.sharedStore.listTasks({
          ...(status ? { status } : {}),
          limit: Number(params.limit ?? 100),
          offset: Number(params.offset ?? 0),
        });
        return { tasks, count: tasks.length };
      }
      case "tasks.get": {
        const task = this.options.sharedStore.getTask(String(params.id ?? params.taskId ?? ""));
        if (!task) throw new GatewayProtocolError("NOT_FOUND", "Unknown task");
        return { task };
      }
      case "tasks.cancel": {
        const id = String(params.id ?? params.taskId ?? "");
        const task = this.options.sharedStore.getTask(id);
        if (!task) throw new GatewayProtocolError("NOT_FOUND", "Unknown task");
        if (task.status !== "running" && task.status !== "queued") return { cancelled: false, task };
        this.#taskCancels.add(id);
        const aborted = this.options.runtime.abort(this.#taskSessions.get(id) ?? task.sessionKey);
        this.broadcast("task", { action: "cancelling", id, aborted });
        return { cancelled: true, aborted, taskId: id };
      }
      case "channels.status":
        return { channels: this.options.channels?.status() ?? [] };
      case "channels.probe":
        if (!this.options.channels) return { ok: false, message: "No channel controller is configured" };
        return this.options.channels.probe(String(params.channel ?? ""), params.accountId ? String(params.accountId) : undefined);
      case "channels.start":
        return { channels: await this.options.channels?.start(params.channel ? String(params.channel) : undefined) };
      case "channels.stop":
        return { channels: await this.options.channels?.stop(params.channel ? String(params.channel) : undefined) };
      case "channels.logout":
        await this.options.channels?.logout(String(params.channel), params.accountId ? String(params.accountId) : undefined);
        return { ok: true };
      case "message.send": {
        if (!this.options.config.tools.externalMessaging) throw new GatewayProtocolError("FORBIDDEN", "External messaging is disabled; set tools.externalMessaging=true and restart the Gateway");
        if (!this.options.channels) throw new GatewayProtocolError("NOT_FOUND", "No channel controller is configured");
        const match = params.sessionKey ? this.#findSession({ key: String(params.sessionKey) }) : undefined;
        const channel = boundedParam(params.channel ?? match?.session.channel, "channel", 128);
        const accountId = boundedParam(params.accountId ?? "default", "accountId", 128);
        const to = boundedParam(params.to ?? params.recipient ?? match?.session.peerId, "recipient", 4_096);
        const text = boundedParam(params.text ?? params.message, "message", 64 * 1024).trim();
        const kind = String(params.kind ?? "dm") as "dm" | "group" | "thread";
        if (!channel || !to || !text || !["dm", "group", "thread"].includes(kind)) throw new GatewayProtocolError("INVALID_PARAMS", "channel, recipient, text, and a valid kind are required");
        const idempotencyKey = String(params.idempotencyKey ?? randomUUID());
        if (idempotencyKey.length > 256) throw new GatewayProtocolError("INVALID_PARAMS", "idempotencyKey is too long");
        if (params.confirm !== true) return { ok: false, requiresApproval: true, idempotencyKey };
        const approvalKey = `message:${channel}:${accountId}:${to}:${idempotencyKey}`;
        const approvalId = this.options.sharedStore.createApproval("external.message", `Send a message to ${channel}:${accountId}:${to}`, { channel, accountId, to, kind, text }, approvalKey);
        const approval = this.options.sharedStore.getApprovalByKey(approvalKey);
        if (approval?.status === "denied") throw new GatewayProtocolError("FORBIDDEN", `Operator denied approval ${approvalId}`);
        if (approval?.status !== "approved") {
          this.broadcast("exec.approval.requested", { id: approvalId, kind: "external.message", channel, to }, "operator.approvals");
          return { ok: false, requiresApproval: true, approvalId, idempotencyKey };
        }
        if (!this.options.sharedStore.consumeApproval(approvalId)) throw new GatewayProtocolError("FORBIDDEN", `Approval is no longer usable: ${approvalId}`);
        const sent = await this.options.channels.send(channel, accountId, {
          conversation: { channel, accountId, id: to, kind, ...(params.threadId ? { threadId: String(params.threadId) } : {}) },
          text,
          ...(params.replyToId ? { replyToId: String(params.replyToId) } : {}),
        });
        return { ok: true, channel, accountId, to, ...sent };
      }
      case "config.get":
        return { config: this.options.config };
      case "config.set": {
        if (!params.config || typeof params.config !== "object") throw new GatewayProtocolError("INVALID_PARAMS", "config is required");
        const next = mergeConfig(structuredClone(this.options.config), params.config);
        await writeConfig(this.options.paths, next);
        Object.assign(this.options.config, next);
        return { ok: true };
      }
      case "config.patch": {
        if (!params.patch || typeof params.patch !== "object") throw new GatewayProtocolError("INVALID_PARAMS", "patch is required");
        const next = mergeConfig(structuredClone(this.options.config), params.patch);
        await writeConfig(this.options.paths, next);
        Object.assign(this.options.config, next);
        return { ok: true };
      }
      case "logs.tail":
        return { lines: this.logger.tail(Number(params.lines ?? 100)) };
      case "pairing.list":
        return { pairings: this.options.sharedStore.listPairings() };
      case "pairing.approve":
        return { approved: this.options.sharedStore.approvePairing(String(params.code ?? "")) };
      case "plugins.list":
        return { allowed: this.options.config.plugins.allow, plugins: this.options.sharedStore.listPlugins() };
      case "approvals.list":
        return { approvals: this.options.sharedStore.listApprovals(params.status ? String(params.status) : undefined) };
      case "approvals.resolve": {
        const decision = String(params.decision ?? "");
        if (decision !== "approved" && decision !== "denied") {
          throw new GatewayProtocolError("INVALID_PARAMS", "decision must be approved or denied");
        }
        const id = String(params.id ?? "");
        const resolved = this.options.sharedStore.resolveApproval(id, decision);
        if (resolved) this.broadcast("exec.approval.resolved", { id, decision }, "operator.approvals");
        return { resolved };
      }
      default:
        throw new GatewayProtocolError("METHOD_NOT_FOUND", `Unsupported gateway method: ${request.method}`);
    }
  }

  async #runDueCronJobs(): Promise<void> {
    for (const job of this.options.sharedStore.dueCronJobs(Date.now())) {
      if (this.#cronRunning.has(job.id)) continue;
      try {
        this.#startCronJob(job, false);
      } catch (error) {
        this.logger.log("error", "Could not start cron job", { jobId: job.id, message: (error as Error).message });
      }
    }
  }

  async #runBootHooks(): Promise<void> {
    if (!hookEnabled(this.options.config, "boot-md")) return;
    for (const agent of this.options.config.agents.list) {
      const workspace = agent.workspace ?? this.options.config.agents.defaults.workspace;
      let instructions: string;
      try {
        const stat = await fs.stat(path.join(workspace, "BOOT.md"));
        if (!stat.isFile() || stat.size > 128 * 1024) throw new Error("BOOT.md must be a regular file no larger than 128 KiB");
        instructions = (await fs.readFile(path.join(workspace, "BOOT.md"), "utf8")).trim();
      } catch (error) {
        if ((error as NodeJS.ErrnoException).code === "ENOENT") continue;
        this.logger.log("warn", "boot-md hook skipped", { agentId: agent.id, message: (error as Error).message });
        continue;
      }
      if (!instructions) continue;
      this.broadcast("hook", { name: "boot-md", event: "gateway:startup", agentId: agent.id });
      for await (const event of this.options.runtime.run({
        text: `Gateway startup hook BOOT.md:\n\n${instructions}`,
        sessionKey: `agent:${agent.id}:hook:boot-md`,
        agentId: agent.id,
      })) {
        if (event.type === "error") this.logger.log("warn", "boot-md hook failed", { agentId: agent.id, message: event.message });
      }
    }
  }

  #startCronJob(job: StoredCronJob, manual: boolean): { accepted: true; jobId: string; taskId: string; runId: string } {
    if (this.#cronRunning.has(job.id)) throw new GatewayProtocolError("CONFLICT", "Cron job is already running");
    const taskId = randomUUID();
    const nextRunAtMs = manual ? job.nextRunAtMs : nextScheduleTime(job.schedule, Date.now());
    const disable = !manual && job.schedule.kind === "at";
    this.options.sharedStore.saveTask({
      id: taskId,
      kind: "cron",
      status: "running",
      agentId: job.agentId,
      sessionKey: job.sessionKey,
      summary: job.name,
    });
    const run = this.options.sharedStore.beginCronRun({
      jobId: job.id,
      taskId,
      ...(nextRunAtMs === undefined ? {} : { nextRunAtMs }),
      disable,
    });
    this.#cronRunning.add(job.id);
    this.#taskSessions.set(taskId, job.sessionKey);
    this.broadcast("cron", { action: "started", jobId: job.id, taskId, runId: run.runId, manual });
    this.broadcast("task", { action: "started", task: this.options.sharedStore.getTask(taskId) });
    void this.#executeCronJob(job, taskId, run.runId);
    return { accepted: true, jobId: job.id, taskId, runId: run.runId };
  }

  async #executeCronJob(job: StoredCronJob, taskId: string, runId: string): Promise<void> {
    let text = "";
    let failureMessage: string | undefined;
    try {
      for await (const event of this.options.runtime.run({ text: job.prompt, sessionKey: job.sessionKey, agentId: job.agentId })) {
        if (event.type === "text-delta") text += event.text;
        if (event.type === "error") failureMessage = event.message;
        this.broadcast("task", { action: "progress", taskId, event });
      }
    } catch (error) {
      failureMessage = (error as Error).message;
    }
    const cancelled = this.#taskCancels.has(taskId);
    const status: "completed" | "failed" | "cancelled" = cancelled ? "cancelled" : failureMessage ? "failed" : "completed";
    this.options.sharedStore.finishCronRun(runId, status, text || undefined, cancelled ? "Cancelled by operator" : failureMessage);
    const task = this.options.sharedStore.saveTask({
      id: taskId,
      kind: "cron",
      status,
      agentId: job.agentId,
      sessionKey: job.sessionKey,
      summary: job.name,
      ...(text ? { result: { text } } : {}),
      ...(cancelled ? { error: "Cancelled by operator" } : failureMessage ? { error: failureMessage } : {}),
    });
    this.broadcast("cron", { action: "finished", jobId: job.id, taskId, runId, status });
    this.broadcast("task", { action: "finished", task });
    this.#cronRunning.delete(job.id);
    this.#taskCancels.delete(taskId);
    this.#taskSessions.delete(taskId);
  }

  #startAgent(params: Record<string, unknown>): { runId: string; accepted: true } {
    const runId = randomUUID();
    const sessionKey = boundedParam(params.sessionKey ?? "agent:main:main", "session key", 512);
    const message = boundedParam(params.message ?? params.text, "agent input", 1024 * 1024);
    let text = "";
    let error: string | undefined;
    const done = (async () => {
      for await (const event of this.options.runtime.run({
        text: message,
        sessionKey,
        ...(params.agentId ? { agentId: String(params.agentId) } : {}),
        ...(params.model ? { model: String(params.model) } : {}),
        ...(Array.isArray(params.media) ? { media: params.media as Array<{ url?: string; data?: string; mediaType: string; name?: string }> } : {}),
      })) {
        if (event.type === "text-delta") text += event.text;
        if (event.type === "error") error = event.message;
        this.broadcast("agent", { runId, sessionKey, event });
      }
      const result = error ? { text, error } : { text };
      this.broadcast("chat", { runId, sessionKey, ...result });
      setTimeout(() => this.runs.delete(runId), 60_000).unref();
      return result;
    })();
    this.runs.set(runId, { done, abort: () => this.options.runtime.abort(sessionKey) });
    return { runId, accepted: true };
  }
}
